import type { PostHog, Properties } from "posthog-js";
import type { Principal } from "@knowledge/sync";

// Production builds receive the public project token from scripts/deploy.ts.
// Without it this module never loads posthog-js or contacts PostHog.
const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string | undefined;
// Keep the region in sync with apps/worker/analytics-proxy.ts.
const uiHost = "https://us.posthog.com";

const client: Promise<PostHog | null> = key
  ? import("posthog-js").then(({ default: posthog }) => {
      posthog.init(key, {
        api_host: `${location.origin}/ingest`,
        ui_host: uiHost,
        defaults: "2026-08-30",
        capture_pageview: "history_change",
        capture_pageleave: true,
        capture_exceptions: {
          capture_unhandled_errors: true,
          capture_unhandled_rejections: true,
          capture_console_errors: false,
        },
        person_profiles: "identified_only",
        session_recording: {
          maskAllInputs: true,
          // Page bodies are user content; the editor is contenteditable.
          maskTextSelector: "[contenteditable='true']",
        },
        // Avoid a cookie riding on every API and WebSocket request.
        persistence: "localStorage",
      });
      return posthog;
    })
  : Promise.resolve(null);

function withClient(use: (posthog: PostHog) => void) {
  void client.then((posthog) => {
    if (posthog) use(posthog);
  });
}
export function capture(event: string, properties?: Properties) {
  withClient((posthog) => posthog.capture(event, properties));
}
export function identifyUser(principal: Principal) {
  withClient((posthog) =>
    posthog.identify(principal.id, {
      email: principal.email,
      name: principal.displayName || undefined,
    }),
  );
}
export function resetUser() {
  withClient((posthog) => posthog.reset());
}
export function setOrganization(id: string) {
  withClient((posthog) => posthog.group("organization", id));
}
export function captureException(error: unknown) {
  withClient((posthog) => posthog.captureException(error));
}
